import { ChangeEvent, useCallback, useState } from "react";
import { RootState } from "app/store/store";
import { useAppDispatch, useAppSelector } from "./useRedux";
import { SelectChangeEvent } from "@mui/material";
import { FocusEvent as ReactFocusEvent } from "react";

import imageCompression from "browser-image-compression";
import * as yup from "yup";

type UpdateFieldAction<T> = {
  field: keyof T;
  value: T[keyof T];
};
type UpdateFieldFunction<T> = (payload: UpdateFieldAction<T>) => {
  type: string;
  payload: UpdateFieldAction<T>;
};

export type NestedKeyOf<T> = T extends object
  ? {
      [K in keyof T & string]: T[K] extends Array<infer U>
        ? U extends object
          ? `${K}[${number}].${NestedKeyOf<U>}` | `${K}[${number}]` | K
          : `${K}[${number}]` | K
        : T[K] extends object
          ? `${K}.${NestedKeyOf<T[K]>}` | K
          : K;
    }[keyof T & string]
  : never;

interface IFile {
  Name: string;
  DataBase64: string;
}

interface IAutocompleteOption {
  ID: string;
  Name: string;
  INN: string;
}

interface IHasId {
  ID: string;
  [key: string]: any;
}

const setNestedValue = <T extends Record<string, any>>(
  obj: T,
  path: string,
  value: any
): T => {
  const pathParts = path.replace(/\[(\d+)\]/g, ".$1").split(".");
  const updatedObj = { ...obj };

  let current: any = updatedObj;
  for (let i = 0; i < pathParts.length - 1; i++) {
    const key = pathParts[i];
    if (Array.isArray(current)) {
      current = [...current];
    } else {
      current = { ...current };
    }
    if (Array.isArray(current[key])) {
      current[key] = [...current[key]];
    } else if (typeof current[key] === "object" && current[key] !== null) {
      current[key] = { ...current[key] };
    } else {
      current[key] = current[key];
    }
    current = current[key];
  }

  const lastKey = pathParts[pathParts.length - 1];
  current[lastKey] = value;

  return updatedObj;
};

export const useFormRedux = <T extends Record<string, any>>(
  selector: (state: RootState) => T,
  updateField: UpdateFieldFunction<T>,
  validationSchema: yup.ObjectSchema<any>
) => {
  const dispatch = useAppDispatch();
  const formState = useAppSelector(selector);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [autocompleteOptions, setAutocompleteOptions] = useState<IHasId[]>([]);

  const handleChange =
    (fieldName: NestedKeyOf<T>) => (e: ChangeEvent<HTMLInputElement>) => {
      const { value } = e.target;

      if (
        typeof fieldName === "string" &&
        (fieldName.includes(".") || fieldName.includes("["))
      ) {
        const pathParts = fieldName.replace(/\[(\d+)\]/g, ".$1").split(".");
        const rootField = pathParts[0] as keyof T;

        const rootValue = Array.isArray(formState[rootField])
          ? [...(formState[rootField] as any[])]
          : { ...formState[rootField] };

        let current: any = rootValue;
        for (let i = 1; i < pathParts.length; i++) {
          const key = pathParts[i];
          if (i === pathParts.length - 1) {
            current[key] = value;
          } else {
            if (Array.isArray(current[key])) {
              current[key] = [...current[key]];
            } else {
              current[key] = { ...current[key] };
            }
            current = current[key];
          }
        }

        dispatch(
          updateField({ field: rootField, value: rootValue as T[keyof T] })
        );
      } else {
        dispatch(
          updateField({
            field: fieldName as keyof T,
            value: value as T[keyof T],
          })
        );
      }
    };
  const handleDateChange =
    (fieldName: keyof T | string) => (e: ChangeEvent<HTMLInputElement>) => {
      let value = e.target.value.replace(/\D/g, "");
      let formattedValue = "";
      if (value.length > 0) {
        formattedValue += value.substring(0, 2);
      }
      if (value.length > 2) {
        formattedValue += "." + value.substring(2, 4);
      }
      if (value.length > 4) {
        formattedValue += "." + value.substring(4, 8);
      }

      if (typeof fieldName === "string" && fieldName.includes(".")) {
        const keys = fieldName.split(".");
        const rootField = keys[0] as keyof T;

        const rootValue = { ...formState[rootField] };

        let current: any = rootValue;
        for (let i = 1; i < keys.length; i++) {
          const key = keys[i];
          if (i === keys.length - 1) {
            current[key] = formattedValue;
          } else {
            current[key] = { ...current[key] };
            current = current[key];
          }
        }
        dispatch(
          updateField({ field: rootField, value: rootValue as T[keyof T] })
        );
      } else {
        dispatch(
          updateField({
            field: fieldName as keyof T,
            value: formattedValue as T[keyof T],
          })
        );
      }
    };

  const handleSelectChange =
    (fieldName: keyof T | string) => (e: SelectChangeEvent<string>) => {
      const value = e.target.value;
      if (typeof fieldName === "string" && fieldName.includes(".")) {
        const keys = fieldName.split(".");
        const rootField = keys[0] as keyof T;
        const rootValue = formState[rootField];
        const updatedRootValue = setNestedValue(rootValue, fieldName, value);
        dispatch(
          updateField({
            field: rootField,
            value: updatedRootValue as T[keyof T],
          })
        );
      } else {
        dispatch(
          updateField({
            field: fieldName as keyof T,
            value: value as T[keyof T],
          })
        );
      }
    };

  const handleNumberChange =
    (fieldName: keyof T | string) => (e: ChangeEvent<HTMLInputElement>) => {
      let value = e.target.value;
      value = value.replace(/[^\d.,]/g, "");
      const decimalSeparator = value.includes(".")
        ? "."
        : value.includes(",")
          ? ","
          : "";
      const parts = value.split(/[.,]/);
      if (parts.length > 1) {
        parts[1] = parts[1].slice(0, 2);
        value = parts.join(decimalSeparator);
      }
      const integerPart = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, " ");
      const formattedValue =
        parts.length > 1
          ? `${integerPart}${decimalSeparator}${parts[1]}`
          : integerPart;
      if (typeof fieldName === "string" && fieldName.includes(".")) {
        const keys = fieldName.split(".");
        const rootField = keys[0] as keyof T;
        const rootValue = formState[rootField];
        const updatedRootValue = setNestedValue(
          rootValue,
          fieldName,
          formattedValue
        );
        dispatch(
          updateField({
            field: rootField,
            value: updatedRootValue as T[keyof T],
          })
        );
      } else {
        dispatch(
          updateField({
            field: fieldName as keyof T,
            value: formattedValue as T[keyof T],
          })
        );
      }
    };

  const handleNumberBlur =
    (fieldName: keyof T | string) =>
    async (e: ReactFocusEvent<HTMLInputElement>) => {
      let value = e.target.value;
      if (!value.includes(".") && !value.includes(",")) {
        value = value.replace(/\s/g, "") + ".00";
        value = value.replace(/\B(?=(\d{3})+(?!\d))/g, " ");
      }
      if (typeof fieldName === "string" && fieldName.includes(".")) {
        const keys = fieldName.split(".");
        const rootField = keys[0] as keyof T;
        const rootValue = formState[rootField];
        const updatedRootValue = setNestedValue(rootValue, fieldName, value);
        dispatch(
          updateField({
            field: rootField,
            value: updatedRootValue as T[keyof T],
          })
        );
      } else {
        dispatch(
          updateField({
            field: fieldName as keyof T,
            value: value as T[keyof T],
          })
        );
      }
      await validateField(fieldName, value);
    };

  const formatPhone = (rawValue: string): string => {
    if (!rawValue) return "";
    const digitsOnly = rawValue.replace(/\D/g, "");
    if (digitsOnly.length < 2) return `8${digitsOnly.substring(1)}`;
    if (digitsOnly.length < 5) return `8(${digitsOnly.substring(1, 4)}`;
    if (digitsOnly.length < 8)
      return `8(${digitsOnly.substring(1, 4)})-${digitsOnly.substring(4, 7)}`;
    if (digitsOnly.length < 10)
      return `8(${digitsOnly.substring(1, 4)})-${digitsOnly.substring(4, 7)}-${digitsOnly.substring(7, 9)}`;
    return `8(${digitsOnly.substring(1, 4)})-${digitsOnly.substring(4, 7)}-${digitsOnly.substring(7, 9)}-${digitsOnly.substring(9, 11)}`;
  };

  const unformatPhone = (formattedValue: string): string => {
    if (!formattedValue) return "";
    return formattedValue.replace(/\D/g, "");
  };

  const handlePhoneChange =
    (fieldName: keyof T | string) => (e: ChangeEvent<HTMLInputElement>) => {
      let rawValue = e.target.value.replace(/\D/g, "");
      if (rawValue.length > 11) {
        rawValue = rawValue.substring(0, 11);
      }

      if (typeof fieldName === "string" && fieldName.includes(".")) {
        const keys = fieldName.split(".");
        const rootField = keys[0] as keyof T;

        const rootValue = { ...formState[rootField] };

        let current: any = rootValue;
        for (let i = 1; i < keys.length; i++) {
          const key = keys[i];
          if (i === keys.length - 1) {
            current[key] = rawValue;
          } else {
            current[key] = { ...current[key] };
            current = current[key];
          }
        }

        dispatch(
          updateField({ field: rootField, value: rootValue as T[keyof T] })
        );
      } else {
        dispatch(
          updateField({
            field: fieldName as keyof T,
            value: rawValue as T[keyof T],
          })
        );
      }
    };

  const handleCheckboxChange =
    (fieldName: keyof T | string) =>
    (e: ChangeEvent<HTMLInputElement>, checked: boolean) => {
      if (typeof fieldName === "string" && fieldName.includes(".")) {
        const keys = fieldName.split(".");
        const rootField = keys[0] as keyof T;
        const rootValue = formState[rootField];
        const updatedRootValue = setNestedValue(rootValue, fieldName, checked);
        dispatch(
          updateField({
            field: rootField,
            value: updatedRootValue as T[keyof T],
          })
        );
      } else {
        dispatch(
          updateField({
            field: fieldName as keyof T,
            value: checked as T[keyof T],
          })
        );
      }
    };

  const formatTime = (rawValue: string): string => {
    if (!rawValue) return "";
    const digitsOnly = rawValue.replace(/\D/g, "");
    if (digitsOnly.length === 0) return "";
    let formattedValue = digitsOnly;
    if (digitsOnly.length >= 1) {
      formattedValue = digitsOnly.substring(0, 2);
      if (digitsOnly.length >= 3) {
        formattedValue += ":" + digitsOnly.substring(2, 4);
        if (digitsOnly.length >= 5) {
          formattedValue += ":" + digitsOnly.substring(4, 6);
        }
      }
    }
    return formattedValue;
  };

  const handleTimeChange =
    (fieldName: keyof T | string) => (e: ChangeEvent<HTMLInputElement>) => {
      const inputValue = e.target.value;
      const formattedValue = formatTime(inputValue);
      if (typeof fieldName === "string" && fieldName.includes(".")) {
        const keys = fieldName.split(".");
        const rootField = keys[0] as keyof T;
        const rootValue = formState[rootField];
        const updatedRootValue = setNestedValue(
          rootValue,
          fieldName,
          formattedValue
        );
        dispatch(
          updateField({
            field: rootField,
            value: updatedRootValue as T[keyof T],
          })
        );
      } else {
        dispatch(
          updateField({
            field: fieldName as keyof T,
            value: formattedValue as T[keyof T],
          })
        );
      }
    };

  const handleTimeBlur =
    (fieldName: keyof T | string) =>
    async (e: ReactFocusEvent<HTMLInputElement>) => {
      const inputValue = e.target.value;
      const digitsOnly = inputValue.replace(/\D/g, "");
      let formattedValue = "";
      if (digitsOnly.length > 0) {
        formattedValue += digitsOnly.substring(0, 2).padStart(2, "0");
      } else {
        formattedValue += "00";
      }
      formattedValue += ":";
      if (digitsOnly.length > 2) {
        formattedValue += digitsOnly.substring(2, 4).padStart(2, "0");
      } else {
        formattedValue += "00";
      }
      formattedValue += ":";
      if (digitsOnly.length > 4) {
        formattedValue += digitsOnly.substring(4, 6).padStart(2, "0");
      } else {
        formattedValue += "00";
      }
      if (typeof fieldName === "string" && fieldName.includes(".")) {
        const keys = fieldName.split(".");
        const rootField = keys[0] as keyof T;
        const rootValue = formState[rootField];
        const updatedRootValue = setNestedValue(
          rootValue,
          fieldName,
          formattedValue
        );
        dispatch(
          updateField({
            field: rootField,
            value: updatedRootValue as T[keyof T],
          })
        );
      } else {
        dispatch(
          updateField({
            field: fieldName as keyof T,
            value: formattedValue as T[keyof T],
          })
        );
      }
      await validateField(fieldName, formattedValue);
    };

  const validateField = async (fieldName: keyof T | string, value: any) => {
    try {
      if (typeof fieldName === "string" && fieldName.includes(".")) {
        const keys = fieldName.split(".");
        const rootField = keys[0] as keyof T;

        const tempFormState = {
          ...formState,
          [rootField]: setNestedValue(formState[rootField], fieldName, value),
        };

        await validationSchema.validateAt(fieldName, tempFormState);
        setErrors((prev) => ({ ...prev, [fieldName]: "" }));
      } else {
        await validationSchema.validateAt(fieldName as string, formState);
        setErrors((prev) => ({ ...prev, [fieldName as string]: "" }));
      }
    } catch (err) {
      if (err instanceof yup.ValidationError) {
        setErrors((prev) => ({ ...prev, [fieldName as string]: err.message }));
      }
    }
  };

  const handleBlur =
    (fieldName: NestedKeyOf<T>) =>
    async (e: ReactFocusEvent<HTMLInputElement>) => {
      const value = e.target.value;
      await validateField(fieldName, value);
    };

  const validateForm = async () => {
    try {
      await validationSchema.validate(formState, { abortEarly: false });
      setErrors({});
      return true;
    } catch (err) {
      if (err instanceof yup.ValidationError) {
        const newErrors: Record<string, string> = {};
        err.inner.forEach((error) => {
          if (error.path) {
            newErrors[error.path] = error.message;
          }
        });
        setErrors(newErrors);
        return false;
      }
      return false;
    }
  };

  const convertFileToBase64 = (file: globalThis.File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const result = reader.result as string;
        resolve(result.split(",")[1]);
      };
      reader.onerror = (error) => reject(error);
      reader.readAsDataURL(file);
    });
  };

  const convertExtToMime = (ext: string): string => {
    const mimeTypes: Record<string, string> = {
      jpg: "image/jpeg",
      jpeg: "image/jpeg",
      png: "image/png",
      xlsx: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      doc: "application/msword",
      docx: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      pdf: "application/pdf",
      txt: "text/plain",
    };
    return mimeTypes[ext] || "";
  };

  const acceptedMimeTypes = [
    "image/jpeg",
    "image/png",
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    "application/msword",
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    "application/pdf",
    "text/plain",
  ];

  const handleFileChange =
    (fieldName: keyof T) => async (e: React.ChangeEvent<HTMLInputElement>) => {
      const input = e.target;
      const newFiles = input.files;
      if (!newFiles || newFiles.length === 0) return;

      const MAX_FILE_SIZE_MB = 20;
      const MAX_TOTAL_FILES = 20;
      const selectedFiles = Array.from(newFiles) as globalThis.File[];

      const currentFiles = (formState[fieldName] as IFile[]) || [];
      if (currentFiles.length + selectedFiles.length > MAX_TOTAL_FILES) {
        alert(`Максимальное количество файлов: ${MAX_TOTAL_FILES}`);
        input.value = "";
        return;
      }

      const invalidFiles = selectedFiles.filter((file) => {
        const fileExt = file.name.split(".").pop()?.toLowerCase() || "";
        const mimeType = convertExtToMime(fileExt);
        return !acceptedMimeTypes.includes(mimeType);
      });

      if (invalidFiles.length > 0) {
        alert(`Недопустимый формат: ${invalidFiles[0].name}`);
        input.value = "";
        return;
      }

      const oversizedFiles = selectedFiles.filter(
        (file) => file.size > MAX_FILE_SIZE_MB * 1024 * 1024
      );

      if (oversizedFiles.length > 0) {
        alert(
          `Файл слишком большой: ${oversizedFiles[0].name} (макс. ${MAX_FILE_SIZE_MB} МБ)`
        );
        input.value = "";
        return;
      }

      try {
        const imageFiles = selectedFiles.filter((file) =>
          ["image/jpeg", "image/png"].includes(file.type)
        );
        const otherFiles = selectedFiles.filter(
          (file) => !["image/jpeg", "image/png"].includes(file.type)
        );

        const options = {
          maxSizeMB: 1,
          maxWidthOrHeight: 1024,
          useWebWorker: true,
        };

        const compressedFiles = await Promise.all(
          imageFiles.map(async (file) => {
            try {
              return await imageCompression(file as globalThis.File, options);
            } catch (error) {
              console.error("Ошибка сжатия:", error);
              return file;
            }
          })
        );

        const allFiles = [...compressedFiles, ...otherFiles];

        const newFileItems: IFile[] = [];
        for (const file of allFiles) {
          const base64Data = await convertFileToBase64(file as globalThis.File);
          newFileItems.push({
            Name: file.name,
            DataBase64: base64Data,
          });
        }

        dispatch(
          updateField({
            field: fieldName,
            value: [...currentFiles, ...newFileItems] as T[keyof T],
          })
        );
      } catch (error) {
        console.error("Ошибка обработки файлов:", error);
        alert("Ошибка при обработке файлов");
      } finally {
        input.value = "";
      }
    };

  const handleRemoveFile = (fieldName: keyof T) => (index: number) => {
    const currentFiles = (formState[fieldName] as IFile[]) || [];
    const updatedFiles = currentFiles.filter((_, i) => i !== index);
    dispatch(
      updateField({ field: fieldName, value: updatedFiles as T[keyof T] })
    );
  };

   const handleAutocompleteChangeID =
        (fieldName: keyof T) =>
            async (value: string) => {
            };

  const handleAutocompleteChange =
    (fieldName: keyof T | string) => async (value: string) => {
      if (
        typeof fieldName === "string" &&
        (fieldName.includes(".") || fieldName.includes("["))
      ) {
        const pathParts = fieldName.replace(/\[(\d+)\]/g, ".$1").split(".");
        const rootField = pathParts[0] as keyof T;

        const rootValue = Array.isArray(formState[rootField])
          ? [...(formState[rootField] as any[])]
          : { ...formState[rootField] };

        let current: any = rootValue;
        for (let i = 1; i < pathParts.length; i++) {
          const key = pathParts[i];
          if (i === pathParts.length - 1) {
            current[key] = value;
          } else {
            if (Array.isArray(current[key])) {
              current[key] = [...current[key]];
            } else {
              current[key] = { ...current[key] };
            }
            current = current[key];
          }
        }

        dispatch(
          updateField({ field: rootField, value: rootValue as T[keyof T] })
        );
      } else {
        dispatch(
          updateField({
            field: fieldName as keyof T,
            value: value as T[keyof T],
          })
        );
      }
    };

  const handleAutocompleteSelect =
    (fieldName: keyof T | string) =>
    (_: React.SyntheticEvent, newValue: IHasId | null) => {
      if (typeof fieldName === "string" && fieldName.includes(".")) {
        const keys = fieldName.split(".");
        const rootField = keys[0] as keyof T;
        const rootValue = formState[rootField];
        const updatedRootValue = setNestedValue(
          rootValue,
          fieldName,
          newValue?.ID || ""
        );
        dispatch(
          updateField({
            field: rootField,
            value: updatedRootValue as T[keyof T],
          })
        );
      } else {
        if (newValue) {
          dispatch(
            updateField({
              field: fieldName as keyof T,
              value: newValue.ID as T[keyof T],
            })
          );
        } else {
          dispatch(
            updateField({
              field: fieldName as keyof T,
              value: "" as T[keyof T],
            })
          );
        }
      }
    };

  const setAutocompleteOptionsList = (options: IAutocompleteOption[]) => {
    setAutocompleteOptions(options);
  };

  const handleMultiAutocompleteChange =
    (fieldName: keyof T | string) =>
    (_: React.SyntheticEvent, newValue: IHasId[]) => {
      const selectedIds = newValue.map((item) => item.ID);
      if (typeof fieldName === "string" && fieldName.includes(".")) {
        const keys = fieldName.split(".");
        const rootField = keys[0] as keyof T;
        const rootValue = formState[rootField];
        const updatedRootValue = setNestedValue(
          rootValue,
          fieldName,
          selectedIds
        );
        dispatch(
          updateField({
            field: rootField,
            value: updatedRootValue as T[keyof T],
          })
        );
      } else {
        dispatch(
          updateField({
            field: fieldName as keyof T,
            value: selectedIds as T[keyof T],
          })
        );
      }
    };

  const handleAddArrayItem = useCallback(
    <K extends keyof T>(
      fieldName: K,
      newItem: T[K] extends Array<infer U> ? U : never
    ) => {
      const currentArray = formState[fieldName] as Array<
        T[K] extends Array<infer U> ? U : never
      >;
      const updatedArray = [...currentArray, newItem];
      dispatch(updateField({ field: fieldName, value: updatedArray as T[K] }));
    },
    [formState, dispatch, updateField]
  );

  const handleUpdateArrayItem = useCallback(
    <K extends keyof T>(
      fieldName: K,
      index: number,
      updatedItem: Partial<Extract<T[K], any[]>[number]>
    ) => {
      const currentArray = formState[fieldName] as Extract<T[K], any[]>[];
      const updatedArray = currentArray.map((item, i) =>
        i === index ? { ...item, ...updatedItem } : item
      );
      dispatch(updateField({ field: fieldName, value: updatedArray as T[K] }));
    },
    [formState, dispatch, updateField]
  );

  const handleRemoveArrayItem = useCallback(
    <K extends keyof T>(fieldName: K, index: number) => {
      const currentArray = formState[fieldName] as Array<
        T[K] extends Array<infer U> ? U : never
      >;
      const updatedArray = currentArray.filter((_, i) => i !== index);
      dispatch(updateField({ field: fieldName, value: updatedArray as T[K] }));
    },
    [formState, dispatch, updateField]
  );

  const handleChangeRadioGroup =
    (fieldName: keyof T | string) =>
    (e: ChangeEvent<HTMLInputElement>, value: string) => {
      if (typeof fieldName === "string" && fieldName.includes(".")) {
        const keys = fieldName.split(".");
        const rootField = keys[0] as keyof T;
        const rootValue = formState[rootField];
        const updatedRootValue = setNestedValue(rootValue, fieldName, value);
        dispatch(
          updateField({
            field: rootField,
            value: updatedRootValue as T[keyof T],
          })
        );
      } else {
        dispatch(
          updateField({
            field: fieldName as keyof T,
            value: value as T[keyof T],
          })
        );
      }
    };

  const handleToggleGenderChange =
    (fieldName: keyof T | string) =>
    (_: React.MouseEvent<HTMLElement>, value: string | null) => {
      if (value) {
        const fakeEvent = {
          target: { value },
        } as React.ChangeEvent<HTMLInputElement>;
        handleChangeRadioGroup(fieldName)(fakeEvent, value);
      }
    };

  const handleToggleInsuredGenderChange =
    (index: number) =>
    (_: React.MouseEvent<HTMLElement>, value: string | null) => {
      if (value) {
        handleUpdateArrayItem("InsuredList" as keyof T, index, {
          Gender: value,
        });
      }
    };
  const formatDocumentNumber = (rawValue: string): string => {
    if (!rawValue) return "";
    const digitsOnly = rawValue.replace(/\D/g, "").slice(0, 6);
    return digitsOnly;
  };

  const formatDocumentSeries = (rawValue: string): string => {
    if (!rawValue) return "";
    const digitsOnly = rawValue.replace(/\D/g, "").slice(0, 4);
    return digitsOnly;
  };

  const formatDepartmentCode = (rawValue: string): string => {
    if (!rawValue) return "";
    const digitsOnly = rawValue.replace(/\D/g, "").slice(0, 6);
    let formattedValue = "";
    if (digitsOnly.length > 0) {
      formattedValue += digitsOnly.substring(0, 3);
    }
    if (digitsOnly.length > 3) {
      formattedValue += "-" + digitsOnly.substring(3, 6);
    }
    return formattedValue;
  };

  const handleDocumentSeriesChange =
    (fieldName: keyof T | string) => (e: ChangeEvent<HTMLInputElement>) => {
      const rawValue = e.target.value;
      const formattedValue = formatDocumentSeries(rawValue);

      if (typeof fieldName === "string" && fieldName.includes(".")) {
        const keys = fieldName.split(".");
        const rootField = keys[0] as keyof T;
        const rootValue = { ...formState[rootField] };
        let current: any = rootValue;
        for (let i = 1; i < keys.length; i++) {
          const key = keys[i];
          if (i === keys.length - 1) {
            current[key] = formattedValue;
          } else {
            current[key] = { ...current[key] };
            current = current[key];
          }
        }
        dispatch(
          updateField({ field: rootField, value: rootValue as T[keyof T] })
        );
      } else {
        dispatch(
          updateField({
            field: fieldName as keyof T,
            value: formattedValue as T[keyof T],
          })
        );
      }
    };

  const handleDocumentNumberChange =
    (fieldName: keyof T | string) => (e: ChangeEvent<HTMLInputElement>) => {
      const rawValue = e.target.value;
      const formattedValue = formatDocumentNumber(rawValue);

      if (typeof fieldName === "string" && fieldName.includes(".")) {
        const keys = fieldName.split(".");
        const rootField = keys[0] as keyof T;
        const rootValue = { ...formState[rootField] };
        let current: any = rootValue;
        for (let i = 1; i < keys.length; i++) {
          const key = keys[i];
          if (i === keys.length - 1) {
            current[key] = formattedValue;
          } else {
            current[key] = { ...current[key] };
            current = current[key];
          }
        }
        dispatch(
          updateField({ field: rootField, value: rootValue as T[keyof T] })
        );
      } else {
        dispatch(
          updateField({
            field: fieldName as keyof T,
            value: formattedValue as T[keyof T],
          })
        );
      }
    };

  const handleDepartmentCodeChange =
    (fieldName: keyof T | string) => (e: ChangeEvent<HTMLInputElement>) => {
      const rawValue = e.target.value;
      const formattedValue = formatDepartmentCode(rawValue);

      if (typeof fieldName === "string" && fieldName.includes(".")) {
        const keys = fieldName.split(".");
        const rootField = keys[0] as keyof T;
        const rootValue = { ...formState[rootField] };
        let current: any = rootValue;
        for (let i = 1; i < keys.length; i++) {
          const key = keys[i];
          if (i === keys.length - 1) {
            current[key] = formattedValue;
          } else {
            current[key] = { ...current[key] };
            current = current[key];
          }
        }
        dispatch(
          updateField({ field: rootField, value: rootValue as T[keyof T] })
        );
      } else {
        dispatch(
          updateField({
            field: fieldName as keyof T,
            value: formattedValue as T[keyof T],
          })
        );
      }
    };

  const handleAddressChange =
    (fieldName: keyof T | string) => (value: string) => {
      if (typeof fieldName === "string" && fieldName.includes(".")) {
        const keys = fieldName.split(".");
        const rootField = keys[0] as keyof T;
        const rootValue = { ...formState[rootField] };

        let current: any = rootValue;
        for (let i = 1; i < keys.length; i++) {
          const key = keys[i];
          if (i === keys.length - 1) {
            current[key] = value;
          } else {
            current[key] = { ...current[key] };
            current = current[key];
          }
        }

        dispatch(
          updateField({ field: rootField, value: rootValue as T[keyof T] })
        );
      } else {
        dispatch(
          updateField({
            field: fieldName as keyof T,
            value: value as T[keyof T],
          })
        );
      }
    };
  const handleArrayDateChange =
    (fieldName: string) => (e: ChangeEvent<HTMLInputElement>) => {
      let value = e.target.value.replace(/\D/g, "");
      let formattedValue = "";

      if (value.length > 0) {
        formattedValue += value.substring(0, 2);
      }
      if (value.length > 2) {
        formattedValue += "." + value.substring(2, 4);
      }
      if (value.length > 4) {
        formattedValue += "." + value.substring(4, 8);
      }

      const pathParts = fieldName.replace(/\[(\d+)\]/g, ".$1").split(".");
      const rootField = pathParts[0] as keyof T;
      const index = parseInt(pathParts[1], 10);
      const nestedField = pathParts[2];

      const currentArray = formState[rootField] as Array<any>;

      const newArray = currentArray.map((item, i) => {
        if (i === index) {
          return { ...item, [nestedField]: formattedValue };
        }
        return item;
      });

      dispatch(
        updateField({ field: rootField, value: newArray as T[keyof T] })
      );
    };

  const handleArrayPhoneChange =
    (fieldName: string) => (e: ChangeEvent<HTMLInputElement>) => {
      let rawValue = e.target.value.replace(/\D/g, "");
      if (rawValue.length > 11) {
        rawValue = rawValue.substring(0, 11);
      }
      const pathParts = fieldName.replace(/\[(\d+)\]/g, ".$1").split(".");
      const rootField = pathParts[0] as keyof T;
      const index = parseInt(pathParts[1], 10);
      const nestedField = pathParts[2];

      const currentArray = formState[rootField] as Array<any>;

      const newArray = currentArray.map((item, i) => {
        if (i === index) {
          return { ...item, [nestedField]: rawValue };
        }
        return item;
      });
      dispatch(
        updateField({ field: rootField, value: newArray as T[keyof T] })
      );
    };
  return {
    formState,
    handleChange,
    handleDateChange,
    handleSelectChange,
    handleNumberChange,
    handleNumberBlur,
    handlePhoneChange,
    formatPhone,
    unformatPhone,
    handleCheckboxChange,
    handleTimeChange,
    formatTime,
    handleTimeBlur,
    handleFileChange,
    handleRemoveFile,
    handleBlur,
    validateForm,
    errors,
    setErrors,
    handleAutocompleteChange,
    handleAutocompleteSelect,
    autocompleteOptions,
    setAutocompleteOptionsList,
    handleMultiAutocompleteChange,
    handleAddArrayItem,
    handleUpdateArrayItem,
    handleRemoveArrayItem,
    handleChangeRadioGroup,
    handleToggleGenderChange,
    handleToggleInsuredGenderChange,
    handleDepartmentCodeChange,
    handleDocumentNumberChange,
    handleDocumentSeriesChange,
    handleAddressChange,
    handleArrayDateChange,
    handleArrayPhoneChange,
    handleAutocompleteChangeID
  };
};
